import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { v4 } from '@leverege/base62-util';
import { WizardActionFactory, WizardStepFactory } from "./Factory.js";
import { renderComponentOrContent, renderFooter } from "./Util.js";
export default function useWizardInternals(opts) {
  const {
    context,
    model,
    value,
    onChange,
    onReset: pOnReset
  } = opts;
  const [activeStepName, setActiveStepName] = useState(model?.startStep);
  const [header, setHeader] = useState(null);
  const [headerIcon, setHeaderIcon] = useState(null);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [nextText, setNextText] = useState(null);
  const [backText, setBackText] = useState(null);
  const wizardId = useRef(v4());
  const setActiveStep = useCallback(name => {
    setActiveStepName(name);
    setHeader(null);
    setHeaderIcon(null);
    setNextDisabled(false);
    setNextText(null);
    setBackText(null);
  }, []);
  const onReset = useCallback(() => {
    setActiveStepName(model?.startStep);
    setHeader(null);
    setHeaderIcon(null);
    setNextDisabled(false);
    setNextText(null);
    setBackText(null);
    pOnReset?.();
  }, [model?.startStep, pOnReset]);
  const activeStep = useMemo(() => {
    return model?.steps[activeStepName];
  }, [activeStepName, model?.steps]);
  const wizardContext = useMemo(() => {
    // TODO:  Can this be more tidy? I don't like that I'm creating it inside the useMemo
    const runAction = ({
      data: actionName,
      value
    }) => {
      const action = WizardActionFactory.get(actionName);
      if (typeof action?.perform === 'function') {
        action.perform(wizardContext, value);
      } else {
        console.warn(`[UIWizard] No action found with type '${actionName}'`);
      }
    };
    return {
      activeStep,
      backText,
      context,
      model,
      nextDisabled,
      nextText,
      onChange,
      onReset,
      runAction,
      setActiveStep,
      setHeader,
      setHeaderIcon,
      setNextDisabled,
      setNextText,
      setBackText,
      wizardData: value,
      wizardId: wizardId.current
    };
  }, [activeStep, backText, context, model, nextDisabled, nextText, onChange, onReset, setActiveStep, value]);
  useEffect(() => {
    const onOpenAction = model?.onOpen ? WizardActionFactory.get(model.onOpen) : null;
    if (typeof onOpenAction?.perform === 'function') {
      onOpenAction.perform(wizardContext);
    }
    return () => {
      const onCloseAction = model?.onClose ? WizardActionFactory.get(model.onClose) : null;
      if (typeof onCloseAction?.perform === 'function') {
        onCloseAction.perform(wizardContext);
      }
    };
  }, []);
  const {
    content,
    footer
  } = useMemo(() => {
    let content = null;
    if (activeStep != null) {
      const step = WizardStepFactory.get(activeStep);
      if (step) {
        content = step.content;
      }
    }
    return {
      content: renderComponentOrContent(content),
      footer: renderFooter(activeStep)
    };
  }, [activeStep]);
  return {
    content,
    footer,
    header,
    headerIcon,
    wizardContext,
    wizardId: wizardId.current
  };
}